import { TouchEventHandler, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BodyRowProps, isTouchDevice, ReactTable } from "@tecorien/mimic";
import { NavigationLinks, useNavigationLinks } from "./NavigationContext";

export function useWithDefaultTableRowNavigationProps<T extends object, TKey extends keyof NavigationLinks>(
  linkGetter: TKey,
  getRowProps?: (row: ReactTable.Row<T>) => Partial<BodyRowProps<T>> | undefined,
) {
  const navigate = useNavigate();
  const navigationLinks = useNavigationLinks();
  const navigationLinkGetter = navigationLinks[linkGetter] as any;

  return useCallback((row: ReactTable.Row<T>): Partial<BodyRowProps<T>> | undefined => {
    const link = navigationLinkGetter(row.original);

    const longPressProps = ((): {
      onTouchCancel?: TouchEventHandler<T> | undefined;
      onTouchEnd?: TouchEventHandler<T> | undefined;
      onTouchMove?: TouchEventHandler<T> | undefined;
      onTouchStart?: TouchEventHandler<T> | undefined;
    } => {
      if (isTouchDevice()) {
        const context = {
          timeout: undefined,
        };

        const start = () => {
          context.timeout = setTimeout(() => {
            window.navigator.vibrate(5);
            navigate(link);
          }, 600) as any;
        };

        const stop = () => {
          clearTimeout(context.timeout);
        };

        return {
          onTouchStart: start,
          onTouchEnd: stop,
          onTouchMove: stop,
        };
      }
      return {};
    })();

    if (link) {
      return {
        to: link,
        onDoubleClick: () => navigate(link),
        ...longPressProps as any,
        ...getRowProps?.(row),
      };
    }
    return getRowProps?.(row);

  }, [ navigate, navigationLinkGetter, getRowProps ]);
}